import React from 'react';
import {
  STANDARD_INVOICE_TYPE_TRANSACTION,
  SIMPLIFIED_INVOICE_TYPE_TRANSACTION,
  INVOICE_TYPES_ENUM,
  TAX_TYPES,
} from '../config/invoice/constants';

import moment from 'moment';
import QR from 'qrcode-base64';
import { Buffer } from 'buffer';
import { printInvoice } from '../services/apis';
import { setSelectOption } from './common';
import { getTaxTypeValue } from '../pages/SmePortal/pages/CreateInvoice/helpers';
import { countryData } from './countryValues';

export const getQrCodeImage = (qrCode) => {
  if (!qrCode) {
    return null;
  }
  return QR.drawImg(qrCode, {
    typeNumber: 4,
    errorCorrectLevel: 'M',
    size: 300,
  });
};

export const getInvoiceTypeTransaction = ({ invoiceType }) => {
  return invoiceType.indexOf('standard') !== -1
    ? STANDARD_INVOICE_TYPE_TRANSACTION
    : SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
};

export const isNoteInvoice = (invoiceType) =>
  invoiceType.toLowerCase().indexOf('note') !== -1;

export const validateItemFields = async ({
  setTouched,
  setErrors,
  validateForm,
  item,
  t,
}) => {
  const formErrors = await validateForm();

  const requiredMessage = t('create_invoice_validation.required');

  const errors = [];

  if (!item.unitPrice || Number(item.unitPrice) <= 0) {
    errors.push({
      key: 'unitPrice',
      value: t('create_invoice_validation.unitPrice_required'),
    });
  }
  if (!item.descriptionEn) {
    errors.push({
      key: 'descriptionEn',
      value: t('create_invoice_validation.descriptionRequired'),
    });
  }

  if (!item.quantity || Number(item.quantity) <= 0) {
    errors.push({
      key: 'quantity',
      value: t('create_invoice_validation.quantityRequired'),
    });
  }
  if (!item.vatRateType) {
    errors.push({
      key: 'vatRateType',
      value: t('create_invoice_validation.vatRateTypeRequired'),
    });
  }
  if (!item.vatRate) {
    errors.push({
      key: 'vatRate',
      value: requiredMessage,
    });
  }

  errors.push({
    key: 'vatRateType',
    value: formErrors?.tempItem?.vatRateType,
  });
  errors.push({
    key: 'descriptionEn',
    value: formErrors?.tempItem?.descriptionEn,
  });
  errors.push({
    key: 'quantity',
    value: formErrors?.tempItem?.quantity,
  });
  errors.push({
    key: 'unitPrice',
    value: formErrors?.tempItem?.unitPrice,
  });
  errors.push({
    key: 'discountAmount',
    value: formErrors?.tempItem?.discountAmount,
  });
  errors.push({
    key: 'discountPercentage',
    value: formErrors?.tempItem?.discountPercentage,
  });
  errors.push({
    key: 'vatRate',
    value: formErrors?.tempItem?.vatRate,
  });

  const errorsToShow = errors
    .filter((error) => !!error.value)
    .reduce(
      (errorsObj, error) => {
        errorsObj.tempItem[error.key] = error.value;

        return errorsObj;
      },
      { tempItem: {} },
    );

  const errorsToTouched = errors
    .filter((error) => !!error.value)
    .reduce(
      (errorsObj, error) => {
        errorsObj.tempItem[error.key] = true;

        return errorsObj;
      },
      { tempItem: {} },
    );
  setTouched(errorsToTouched);
  setErrors(errorsToShow);

  return errors.filter((error) => !!error.value).length > 0;
};

export const calculateItemSubtotalAmount = ({ quantity, unitPrice }) =>
  quantity * unitPrice;

export const calculateItemDiscountAmount = ({
  subtotalAmount,
  discountAmount,
  discountPercentage,
}) => {
  const discountCalculation =
    +discountPercentage > 0 ? (+subtotalAmount * +discountPercentage) / 100 : 0;
  return discountAmount ? +discountAmount : discountCalculation;
};

export const calculateItemDiscountPercentage = ({
  subtotalAmount,
  discountAmount,
  discountPercentage,
}) => {
  if (discountPercentage) {
    return discountPercentage;
  } else {
    return (discountAmount / subtotalAmount) * 100;
  }
};

export const calculateItemTotalAfterDiscount = ({ subtotalAmount, discount }) =>
  +subtotalAmount - +discount;

export const calculateVatAmount = ({ totalAmountAfterDiscount, vatRate }) => {
  const intVatRate = parseInt(vatRate, 10);
  // Handle NaN, 0, and undefined cases - always return 0 for non-positive rates
  if (!intVatRate || intVatRate <= 0 || isNaN(intVatRate)) {
    return 0;
  }
  return (totalAmountAfterDiscount * intVatRate) / 100;
};

export const calculateTotalAmountIncludingTax = ({
  totalAmountAfterDiscount,
  vatAmount,
}) => +totalAmountAfterDiscount + +vatAmount;

export const getProductWithTotals = ({ item, valueToGet }) => {
  const subtotalAmount = calculateItemSubtotalAmount({
    quantity: +item.quantity,
    unitPrice: +item.unitPrice,
  });
  const roundedSubtotalAmount = +Number(subtotalAmount).toFixed(3);
  const discount = calculateItemDiscountAmount({
    subtotalAmount: roundedSubtotalAmount,
    discountAmount: +item.discountAmount,
    discountPercentage: +item.discountPercentage,
  });
  const roundedDiscount = +Number(discount).toFixed(3);

  const discountPercentage = calculateItemDiscountPercentage({
    subtotalAmount: roundedSubtotalAmount,
    discountAmount: item.discountAmount,
    discountPercentage: item.discountPercentage,
  });

  const totalAmountAfterDiscount = calculateItemTotalAfterDiscount({
    subtotalAmount: roundedSubtotalAmount,
    discount: roundedDiscount,
  });
  const roundedTotalAmountAfterDiscount = +Number(
    totalAmountAfterDiscount,
  ).toFixed(3);
  const vatAmount = calculateVatAmount({
    totalAmountAfterDiscount: roundedTotalAmountAfterDiscount,
    vatRate: item.vatRate,
  });
  const roundedVatAmount = +Number(vatAmount).toFixed(3);

  const totalAmountIncludingTax = calculateTotalAmountIncludingTax({
    totalAmountAfterDiscount: roundedTotalAmountAfterDiscount,
    vatAmount: roundedVatAmount,
  });

  const tempObj = {
    subtotalAmount: roundedSubtotalAmount,
    discount: roundedDiscount,
    discountPercentage,
    totalAmountAfterDiscount: roundedTotalAmountAfterDiscount,
    netAmount: roundedTotalAmountAfterDiscount,
    vatAmount: roundedVatAmount,
    totalAmountIncludingTax,
  };

  if (valueToGet) {
    return Number(tempObj[valueToGet]).toFixed(3);
  }

  item.subtotalAmount = Number(subtotalAmount).toFixed(3);
  item.discountPercentage = Number(discountPercentage).toFixed(2);
  item.discountAmount = Number(discount).toFixed(3);
  item.netAmount = Number(totalAmountAfterDiscount).toFixed(3);
  item.vatAmount = Number(vatAmount).toFixed(3);
  item.totalAmount = Number(totalAmountIncludingTax).toFixed(3);
  item.description = (
    <div className="d-flex flex-column">
      <span style={{ fontWeight: 600 }}>{item.descriptionAr}</span>
      <span style={{ fontWeight: 600 }}>{item.descriptionEn}</span>
    </div>
  );

  return item;
};

export const getVatRate = (vatCode) => {
  return TAX_TYPES()[vatCode];
};

export const onVatRateChange = (
  e,
  {
    vatTypes,
    setFieldValue,
    item,
    vatRateKey = 'tempItem.vatRate',
    vatAmountKey = 'tempItem.vatAmount',
    netAmountKey = 'tempItem.netAmount',
    totalAmountKey = 'tempItem.totalAmount',
  },
) => {
  const vatType = vatTypes.filter((type) => type.value === e);
  if (vatType.length) {
    const vatRate = vatType[0].rate + '%';
    item.vatRate = vatRate;
    const vatAmount = getProductWithTotals({ item, valueToGet: 'vatAmount' });
    const netAmount = getProductWithTotals({ item, valueToGet: 'netAmount' });
    const totalAmount = getProductWithTotals({
      item,
      valueToGet: 'totalAmountIncludingTax',
    });
    setFieldValue(vatRateKey, vatRate);
    if (!isNaN(vatAmount)) {
      setFieldValue(vatAmountKey, vatAmount);
    }
    if (!isNaN(netAmount)) {
      setFieldValue(netAmountKey, netAmount);
    }
    if (!isNaN(totalAmount)) {
      setFieldValue(totalAmountKey, totalAmount);
    }
  }
};

export const onValuesBlur = ({
  item,
  setFieldValue,
  vatAmountKey = 'tempItem.vatAmount',
  netAmountKey = 'tempItem.netAmount',
  totalAmountKey = 'tempItem.totalAmount',
}) => {
  const vatAmount = getProductWithTotals({ item, valueToGet: 'vatAmount' });
  const netAmount = getProductWithTotals({ item, valueToGet: 'netAmount' });
  const totalAmount = getProductWithTotals({
    item,
    valueToGet: 'totalAmountIncludingTax',
  });
  if (!isNaN(vatAmount)) {
    setFieldValue(vatAmountKey, vatAmount);
  }
  if (!isNaN(netAmount)) {
    setFieldValue(netAmountKey, netAmount);
  }
  if (!isNaN(totalAmount)) {
    setFieldValue(totalAmountKey, totalAmount);
  }
};

export const setOriginalInvoiceGross = ({
  e,
  previousInvoices,
  setFieldValue,
}) => {
  const gross = previousInvoices.filter((invoice) => invoice.irn === e)[0]
    .originalGrossAmount;
  setFieldValue('originalGrossAmount', gross);
};

export const calculateTotalExcludingTaxView = (items) =>
  items.reduce((amount, item) => +item.subtotalAmount + amount, 0).toFixed(3);

export const calculateTotalVatAmount = (items, values) => {
  const lineItemsVat = items.reduce(
    (amount, item) => +item.vatAmount + amount,
    0,
  );
  const discountVat = values?.einvoiceLevelDiscount
    ? Number(
        calculateInvoiceLevelDiscountVatAmount(
          values?.invoiceDiscountLineItems ?? [],
        ),
      )
    : 0;
  return (lineItemsVat - discountVat).toFixed(3);
};

export const calculateTotalDiscountAmount = (items) =>
  items.reduce((amount, item) => +item.discountAmount + amount, 0).toFixed(3);

const calculatePrepaymentVatAmountValue = (item) => {
  const taxableAmount = Number(
    item?.taxableAmount ?? item?.prepaymentTaxableAmount ?? 0,
  );
  const vatRate = Number(
    item?.prepaymentVatRate ?? getTaxTypeValue(item?.vatRate ?? 0),
  );

  return (taxableAmount * vatRate) / 100;
};

const calculatePrepaymentPaidAmountValue = (item) => {
  const taxableAmount = Number(
    item?.taxableAmount ?? item?.prepaymentTaxableAmount ?? 0,
  );
  return taxableAmount + calculatePrepaymentVatAmountValue(item);
};

const calculateInvoiceGrossTotalValue = (items, values) => {
  const itemsTotal = items.reduce(
    (amount, item) => +item.totalAmount + amount,
    0,
  );
  const invoiceDiscountNetAmount = values?.einvoiceLevelDiscount
    ? Number(
        calculateInvoiceTotalDiscountAmount(
          values?.invoiceDiscountLineItems ?? [],
        ),
      )
    : 0;
  const invoiceDiscountVatAmount = values?.einvoiceLevelDiscount
    ? Number(
        calculateInvoiceLevelDiscountVatAmount(
          values?.invoiceDiscountLineItems ?? [],
        ),
      )
    : 0;

  return itemsTotal - invoiceDiscountNetAmount - invoiceDiscountVatAmount;
};

export const calculateTotalExcludingTax = (items, values) => {
  const itemsNetAmount = items.reduce(
    (amount, item) => +(item.netAmount ?? item.subtotalAmount ?? 0) + amount,
    0,
  );
  const invoiceDiscountNetAmount = values?.einvoiceLevelDiscount
    ? Number(
        calculateInvoiceTotalDiscountAmount(
          values?.invoiceDiscountLineItems ?? [],
        ),
      )
    : 0;

  return (itemsNetAmount - invoiceDiscountNetAmount).toFixed(3);
};

export const calculateTotalIncludingTax = (items, values) =>
  calculateInvoiceGrossTotalValue(items, values).toFixed(3);

export const calculateTotalPayableAmount = (items, values) => {
  const grossTotal = calculateInvoiceGrossTotalValue(items, values);
  const roundingAmount = Number(values?.roundingAmount ?? 0);
  const prepaidAmount = values?.invoicePrepaymentLineItems?.length
    ? Number(calculatePaymentTotal(values.invoicePrepaymentLineItems))
    : 0;
  return (grossTotal + roundingAmount - prepaidAmount).toFixed(3);
};

const ARABIC_CURRENCY_LABELS = Object.values(countryData).reduce(
  (labels, country) => {
    if (country?.currency?.en && country?.currency?.ar) {
      labels[country.currency.en] = country.currency.ar;
    }

    return labels;
  },
  {},
);

export const getLocalizedCurrencyLabel = (
  currency = 'BHD',
  language = 'en',
) => {
  if (!currency) {
    return '';
  }

  if (!language?.startsWith('ar')) {
    return currency;
  }

  if (ARABIC_CURRENCY_LABELS[currency]) {
    return ARABIC_CURRENCY_LABELS[currency];
  }

  try {
    return (
      new Intl.DisplayNames([language], { type: 'currency' }).of(currency) ||
      currency
    );
  } catch (_error) {
    return currency;
  }
};

export const formatNumberWithCurrency = (
  number,
  currency = 'BHD',
  language = 'en',
) =>
  `${isNaN(number) ? '0.000' : number} ${getLocalizedCurrencyLabel(
    currency,
    language,
  )}`;

export const calculateInvoiceTotalDiscountAmount = (items) =>
  items
    .reduce((amount, item) => {
      if (item.discountAmount) {
        return amount + Number(item.discountAmount);
      }

      const discountAmount =
        Number(item.amountSubjectToDiscount) * (Number(item.rate) / 100);
      return amount + discountAmount;
    }, 0)
    .toFixed(3);

export const calculateInvoiceLevelDiscountVatAmount = (items) =>
  items
    .reduce((amount, item) => {
      const vatRate = Number(item.discountVatRate ?? 0);
      const discountBase = Number(item.discountAmount)
        ? Number(item.discountAmount)
        : (Number(item.amountSubjectToDiscount) * Number(item.rate)) / 100;
      return amount + (discountBase * vatRate) / 100;
    }, 0)
    .toFixed(3);

export const normalizeInvoiceLevelDiscountItem = (item) => {
  if (!item) {
    return item;
  }

  const hasDiscountAmount =
    item.discountAmount !== '' &&
    item.discountAmount !== null &&
    item.discountAmount !== undefined;
  const hasSubjectAmount =
    item.amountSubjectToDiscount !== '' &&
    item.amountSubjectToDiscount !== null &&
    item.amountSubjectToDiscount !== undefined;
  const hasRate =
    item.rate !== '' && item.rate !== null && item.rate !== undefined;
  const isSubjectAmount =
    item.type === 'subject_amount' ||
    (!hasDiscountAmount && (hasSubjectAmount || hasRate));
  const discountVatRate =
    item.discountVatRate !== '' &&
    item.discountVatRate !== null &&
    item.discountVatRate !== undefined
      ? +Number(item.discountVatRate).toFixed(3)
      : item.discountVatRateType
      ? +Number(getVatRateFromValue(item.discountVatRateType)).toFixed(3)
      : null;

  return {
    ...item,
    discountAmount:
      !isSubjectAmount && hasDiscountAmount
        ? +Number(item.discountAmount).toFixed(3)
        : null,
    amountSubjectToDiscount:
      isSubjectAmount && hasSubjectAmount
        ? +Number(item.amountSubjectToDiscount).toFixed(3)
        : null,
    rate: isSubjectAmount && hasRate ? +Number(item.rate).toFixed(2) : null,
    discountVatRate,
    taxExemptionReason: item.taxExemptionReason || null,
  };
};

export const calculatePaymentTotal = (items) => {
  return items
    .reduce((acc, item) => {
      return acc + calculatePrepaymentPaidAmountValue(item);
    }, 0)
    .toFixed(3);
};

const handleSpecialScenario = (specialScenario, type) => {
  if (!specialScenario) {
    return false;
  }
  return !!type;
};

const getReasonByVatRateType = (vatRateType, data) => {
  switch (vatRateType) {
    case 'Z':
      return data.zeroRatingReasonInput;
    case 'O':
      return data.outOfScopeReasonInput;
    case 'E':
      return data.exemptionReasonCode;
  }
};

const mapPrepaymentToApi = (prepaymentLineItems) => {
  return prepaymentLineItems.map((prepayment) => {
    // Accept both the form shape and the API DTO shape.
    if (
      prepayment.prepaymentInvoiceNumber ||
      prepayment.prepaymentInvoiceIssueDate ||
      prepayment.prepaymentTaxableAmount ||
      prepayment.prepaymentVatRate ||
      prepayment.prepaidVatAmount ||
      prepayment.paidAmount
    ) {
      return {
        prepaymentInvoiceNumber: prepayment.prepaymentInvoiceNumber || '',
        prepaymentInvoiceIssueDate: prepayment.prepaymentInvoiceIssueDate
          ? moment(prepayment.prepaymentInvoiceIssueDate).format('YYYY-MM-DD')
          : '',
        prepaymentTaxableAmount: +Number(
          prepayment.prepaymentTaxableAmount || 0,
        ).toFixed(3),
        prepaymentVatRate: +Number(prepayment.prepaymentVatRate || 0).toFixed(
          3,
        ),
        // prepaymentVatRateType: prepayment.prepaymentVatRateType || '',
        prepaidVatAmount: +Number(prepayment.prepaidVatAmount || 0).toFixed(3),
        paidAmount: +Number(prepayment.paidAmount || 0).toFixed(3),
      };
    } else {
      return {
        prepaymentInvoiceNumber: prepayment.invoiceNumber || '',
        prepaymentInvoiceIssueDate: prepayment.invoiceIssueDate
          ? moment(prepayment.invoiceIssueDate, 'DD/MM/YYYY').format(
              'YYYY-MM-DD',
            )
          : '',
        prepaymentTaxableAmount: +Number(prepayment.taxableAmount || 0).toFixed(
          3,
        ),
        prepaymentVatRate: +Number(getTaxTypeValue(prepayment.vatRate)).toFixed(
          3,
        ),
        // prepaymentVatRateType: prepayment.vatRate
        //   ? getVatRateSymbolFromValue(prepayment.vatRate)
        //   : '',
        prepaidVatAmount:
          +calculatePrepaymentVatAmountValue(prepayment).toFixed(3),
        paidAmount: +calculatePrepaymentPaidAmountValue(prepayment).toFixed(3),
      };
    }
  });
};

export const convertFormDataToApi = ({ data, category, type, taxCountry }) => {
  if (!data) {
    return null;
  }
  const items = [...data.items];

  const totalExcludingTax = calculateTotalExcludingTax(items, data);
  const totalDiscountsAmount = calculateTotalDiscountAmount(items);
  const totalVatAmount = calculateTotalVatAmount(items, data);
  const totalIncludingVat = calculateTotalIncludingTax(items, data);
  const totalPayableAmount = calculateTotalPayableAmount(items, data);
  const prepaymentLineItems =
    data.invoicePrepaymentLineItems ?? data.prepayment ?? [];
  const prepayment = mapPrepaymentToApi(prepaymentLineItems);

  const supplyDate = moment(data.supplyDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
  const paymentDate = moment(data.paymentDate, 'DD/MM/YYYY').format(
    'YYYY-MM-DD',
  );

  const formData = {
    invoiceUniqueIdentifier: data?.invoiceUniqueIdentifier || '',
    vatNumber: data.vatNumber || '',
    createdDate: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
    invoice_umber: data.invoiceNumber,
    buyerInvoiceNumber: data.buyerInvoiceNumber,
    currency: data.currency,
    exchangeRate: data?.exchangeRate ?? '',
    invoiceStatus: data.invoiceStatus,
    invoiceIssueDate: moment().format('YYYY-MM-DD h:mm:ss'),
    logoImage: data.logo ?? '',
    invoiceType: INVOICE_TYPES_ENUM({
      category,
      type,
      noteType: data.noteType,
    }),
    invoiceTypeTransaction: getInvoiceTypeTransaction({
      invoiceType: category,
    }),
    noteType: data.noteType || null,
    invoiceReferenceNumber: data.invoiceReferenceNumber,
    originalInvoiceReference: data.precedingInvoiceNumber,
    reasonOfNote: data.noteIssuingReason || null,
    arNotes: data.notesAr || data.notesEn,
    enNotes: data.notesEn,
    supplyDate: supplyDate === 'Invalid date' ? '' : supplyDate ?? '',
    paymentDate: paymentDate === 'Invalid date' ? '' : paymentDate ?? '',
    purchaseOrderNumber: data.purchaseOrderNumber,
    contactNumber: data.contactNumber || '',
    salesOrderNumber: data.salesOrderReference,
    specialTaxTreatmentEn: data.specialTaxTreatmentEn,
    specialTaxTreatmentAr: data.specialTaxTreatmentEn,
    invoiceLevelDiscount: (data.invoiceDiscountLineItems ?? []).map((item) => {
      const {
        id,
        type: discountType,
        ...rest
      } = normalizeInvoiceLevelDiscountItem(item);
      return {
        ...rest,
        discountVatRateType: rest.discountVatRateType
          ? getVatRateSymbolFromValue(rest.discountVatRateType)
          : rest.discountVatRateType,
      };
    }),
    seller: {
      vatNumber: data.sellerVatNumber || '',
      sellerEnglishName: data.sellerEnglishName,
      sellerArabicName: data.sellerEnglishName,
      additionalSellerIdType: setSelectOption(
        data.additionalSellerIdType,
        null,
      ),
      additionalSellerId: data.additionalSellerId,
      sellerStreetEn: data.sellerStreetEn,
      sellerStreetAr: data.sellerStreetEn,
      sellerBlock: data.sellerBlock,
      sellerBuildingName: data.sellerBuildingName,
      sellerFlatNumber: data.sellerFlatNumber,
      sellerCity: data.sellerCity,
      sellerCountryCode: data.sellerCountryCode,
    },
    buyer: {
      buyerEnglishName: data.buyerEnglishName,
      buyerArabicName: data.buyerEnglishName,
      buyerVatNumber: data.buyerVatNumber,
      additionalBuyerIdType: setSelectOption(data.additionalBuyerIdType, null),
      additionalBuyerId: data.additionalBuyerId,
      buyerStreetEn: data.buyerStreetEn,
      buyerStreetAr: data.buyerStreetEn,
      buyerCity: data.buyerCity,
      buyerCountryCode: data.buyerCountryCode,
      buyerFlatNumber: data.buyerFlatNumber,
      buyerBuildingName: data.buyerBuildingName,
      buyerBlock: data.buyerBlock,
    },
    paymentTerms: {
      paymentMethodCode: data.paymentMethod || null,
      bankNameEn: data.paymentMethod ? data.bankNameEn : null,
      bankNameAr: data.paymentMethod ? data.bankNameEn : null,
      accountNumber: data.paymentMethod ? data.accountNumber : null,
      paymentTermsEn: data.paymentTermsEn || null,
      paymentTermsAr: data.paymentTermsEn || null,
      accountName: data.paymentMethod ? data.accountName : null,
    },
    totalAmountExcludingVat: +totalExcludingTax,
    totalDiscountsAmount: +totalDiscountsAmount,
    totalVatAmount: +totalVatAmount,
    totalPayableAmount: +totalPayableAmount,
    totalIncludingVat: +totalIncludingVat,
    grossTotalIncludingVat: +totalIncludingVat,
    prepayment,
    invoiceItemDTOList: items.map((item) => {
      const itemData = {
        descriptionAr: item.descriptionEn,
        descriptionEn: item.descriptionEn,
        unitPrice: +item.unitPrice,
        quantity: +item.quantity,
        subtotalAmount: +item.netAmount,
        discountAmount: +item.discountAmount,
        discountPercentage: +item.discountPercentage,
        vatRateType: getVatRateCode(taxCountry, item.vatRateType),
        vatRate: parseInt(item.vatRate, 10),
        vatAmount: +item.vatAmount,
        totalAmount: +item.totalAmount,
        uuid: item.id,
      };

      if (['Z', 'O', 'E'].includes(itemData.vatRateType)) {
        itemData.taxExemptionReason = getReasonByVatRateType(
          itemData.vatRateType,
          data,
        );
      }

      return {
        ...itemData,
      };
    }),
    deemedInvoiceTransaction: handleSpecialScenario(
      data.specialScenario,
      data.deemedSupply,
    ),
    exportsInvoiceTransaction: handleSpecialScenario(
      data.specialScenario,
      data.throughRCMCheck && data.throughRCM === 'Export',
    ),
    summaryInvoiceTransaction: handleSpecialScenario(
      data.specialScenario,
      data.summaryEinvoice,
    ),
    thirdPartyInvoiceTransaction: handleSpecialScenario(
      data.specialScenario,
      data.specialBillingCheck && data.specialBilling === 'Third',
    ),
    selfBilledInvoiceTransaction: handleSpecialScenario(
      data.specialScenario,
      data.specialBillingCheck && data.specialBilling === 'Self',
    ),
    importOfService: handleSpecialScenario(
      data.specialScenario,
      data.throughRCMCheck && data.throughRCM === 'Import',
    ),
    supplyTransaction: false,
    domesticTransaction: false,

    invoicingPeriodStartDate: data.invoicingPeriodStartDate
      ? moment(data.invoicingPeriodStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
      : null,
    invoicingPeriodEndDate: data.invoicingPeriodEndDate
      ? moment(data.invoicingPeriodEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
      : null,
    precedingInvoiceIssueDate: data.precedingInvoiceIssueDate
      ? moment(data.precedingInvoiceIssueDate, 'DD/MM/YYYY').format(
          'YYYY-MM-DD',
        )
      : null,
    roundingAmount: data.roundingAmountSwitch ? +data.roundingAmount || 0 : 0,
  };
  return formData;
};

export const downloadXml = (xml, name) => {
  const decodedXml = Buffer.from(xml, 'base64');
  const fileName = name + '.xml';
  const a = document.createElement('a');
  const file = new Blob([decodedXml], { type: 'text/plain' });
  a.setAttribute('href', window.URL.createObjectURL(file));
  a.setAttribute('download', fileName);
  a.dataset.downloadurl = ['text/plain', a.download, a.href].join(':');
  a.click();
};

export const convertDataToPrint = (data, currency, uuid) => {
  const items = [...data.invoiceItems];
  const values = {
    einvoiceLevelDiscount: (data.invoiceLevelDiscount || []).length > 0,
    invoiceDiscountLineItems: data.invoiceLevelDiscount || [],
    roundingAmount: data.roundingAmount || 0,
    invoicePrepaymentLineItems: data.prepayment ?? [],
  };

  const totalExcludingTax = calculateTotalExcludingTax(items, values);
  const totalDiscountsAmount = calculateTotalDiscountAmount(items);
  const totalVatAmount = calculateTotalVatAmount(items, values);
  const totalIncludingVat = calculateTotalIncludingTax(items, values);
  const totalPayableAmount = calculateTotalPayableAmount(items, values);

  return {
    invoiceReferenceNumber: uuid,
    qrCode: getQrCodeImage(data.qrCode),
    einvoiceDTO: {
      uniqueReferenceIdentifier: uuid,
      ...data,
      invoiceItems: data.invoiceItems.map((item) => ({
        ...item,
        currency,
      })),
      totalAmounts: {
        discountTotalAmount: +totalDiscountsAmount,
        totalExcludingVat: +totalExcludingTax,
        vatTotalAmount: +totalVatAmount,
        grossTotalIncludingVat: +totalIncludingVat,
        totalPayableAmount: +totalPayableAmount,
      },
      currency,
    },
  };
};

export const downloadPDFInvoice = ({ data, currency, uuid, setLoading }) => {
  const requestBody = convertDataToPrint(data, currency, uuid);
  printInvoice(uuid)
    .then((resp) => {
      const pdfResult = resp.data;
      const file = new Blob([pdfResult], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;

      const issueDate = moment(
        requestBody.einvoiceDTO?.invoiceIssueDate,
        'DD-MM-YYYY',
      );
      const vatNumber = requestBody.einvoiceDTO?.seller?.vatNumber;
      const irn = requestBody.invoiceReferenceNumber;

      link.download = `${vatNumber}_${issueDate.format('YYYYMMDD')}_${irn}.pdf`;

      link.click();
      setLoading(false);
    })
    .catch(() => {
      setLoading(false);
    });
};

export const handleSupplyStartDateChange = ({
  setFieldValue,
  setFieldTouched,
  value,
}) => {
  setFieldTouched('supplyStartDate', true);
  setFieldValue('supplyStartDate', value);
  setFieldValue('supplyEndDate', '');
};

export const getTaxTypes = (country, lang) => {
  return lang === 'en'
    ? [
        { label: 'Add VAT Rate', value: '', symbol: '', rate: '' },
        { label: '0%', value: 'Z', symbol: 'Z', rate: '0' },
        {
          label: '5%',
          value: '5',
          symbol: 'S',
          rate: '5',
        },
        {
          label: '10%',
          value: '10',
          symbol: 'S',
          rate: '10',
        },
        { label: 'Exempt', value: 'E', symbol: 'E', rate: '0' },
        {
          label: 'Out of scope',
          value: 'O',
          symbol: 'O',
          rate: '0',
        },
      ]
    : [
        { label: 'إضافة معدل الضريبة', value: '', symbol: '' },
        { label: '0%', value: 'Z', symbol: 'Z', rate: '0' },
        {
          label: '5%',
          value: '5',
          symbol: 'S',
          rate: '5',
        },
        {
          label: '10%',
          value: '10',
          symbol: 'S',
          rate: '10',
        },
        { label: 'معفاة', value: 'E', symbol: 'E', rate: '0' },
        {
          label: 'خارج النطاق',
          value: 'O',
          symbol: 'O',
          rate: '0',
        },
      ];
};

const getVatRateCode = (taxCountry, vatRateType) => {
  const typesObj = getTaxTypes(taxCountry, 'en');
  return typesObj.filter((type) => vatRateType === type.value)[0]?.symbol;
};

// Map from symbol (S, E, O, Z) to value (5, 10, E, O, Z)
// When rate is provided, it disambiguates S between 5% and 10%
export const getVatRateValueFromSymbol = (symbol, taxCountry = 'BH', rate) => {
  const typesObj = getTaxTypes(taxCountry, 'en');
  if (rate !== undefined && rate !== null) {
    const found = typesObj.find(
      (type) => type.symbol === symbol && type.rate === String(rate),
    );
    if (found) {
      return found.value;
    }
  }
  const found = typesObj.find((type) => type.symbol === symbol);
  return found?.value || '';
};

export const getVatRateFromSymbol = (symbol, taxCountry = 'BH', rate) => {
  const typesObj = getTaxTypes(taxCountry, 'en');
  if (rate !== undefined && rate !== null) {
    const found = typesObj.find(
      (type) => type.symbol === symbol && type.rate === String(rate),
    );
    if (found) {
      return found.rate;
    }
  }
  const found = typesObj.find((type) => type.symbol === symbol);
  return found?.rate || '';
};

// Map from value to label for display
export const getVatRateLabelFromValue = (
  value,
  lang = 'en',
  taxCountry = 'BH',
) => {
  const typesObj = getTaxTypes(taxCountry, lang);
  const found = typesObj.find((type) => type.value === value);
  return found?.label || value;
};

// Map from value (dropdown selection) to rate percentage
export const getVatRateFromValue = (value, taxCountry = 'BH') => {
  const typesObj = getTaxTypes(taxCountry, 'en');
  const found = typesObj.find((type) => type.value === value);
  return found?.rate || '';
};

// Map from value (dropdown selection) to backend symbol
export const getVatRateSymbolFromValue = (value, taxCountry = 'BH') => {
  const typesObj = getTaxTypes(taxCountry, 'en');
  const found = typesObj.find((type) => type.value === value);
  return found?.symbol || value;
};

export const convertApiDataToForm = (invoice) => {
  if (!invoice) {
    return null;
  }

  const prepaymentItems = Array.isArray(invoice.prepayment)
    ? invoice.prepayment
    : invoice.prepayment
    ? [invoice.prepayment]
    : [];

  const totalPaidAmount = prepaymentItems.reduce((sum, prepayment) => {
    const paidAmount = Number(
      prepayment?.paidAmount ??
        Number(prepayment?.prepaymentTaxableAmount || 0) +
          Number(prepayment?.prepaidVatAmount || 0),
    );
    return sum + (isNaN(paidAmount) ? 0 : paidAmount);
  }, 0);

  let outOfScopeReasonInput, exemptionReasonCode, zeroRatingReasonInput;
  const items = (invoice.invoiceItems || []).map((item) => {
    switch (item.vatRateType) {
      case 'Z':
        zeroRatingReasonInput = item.taxExemptionReason;
        break;
      case 'E':
        exemptionReasonCode = item.taxExemptionReason;
        break;
      case 'O':
        outOfScopeReasonInput = item.taxExemptionReason;
        break;
    }
    return {
      id: item.uuid || '',
      descriptionAr: item.descriptionAr || '',
      descriptionEn: item.descriptionEn || '',
      unitPrice: item.unitPrice?.toString() || '',
      quantity: item.quantity?.toString() || '',
      discountAmount: item.discountAmount?.toString() || '',
      discountPercentage: item.discountPercentage?.toString() || '',
      vatRateType:
        getVatRateValueFromSymbol(item.vatRateType, 'BH', item.vatRate) || '',
      vatRate: item.vatRate?.toString() || '',
      netAmount: item.subtotalAmount?.toString() || '',
      subtotalAmount: item.subtotalAmount?.toString() || '',
      vatAmount: item.vatAmount?.toString() || '',
      totalAmount: item.totalAmount?.toString() || '',
      creating: false,
    };
  });

  const invoiceIssueDate = invoice.invoiceIssueDate
    ? moment(invoice.invoiceIssueDate).format('YYYY-MM-DD')
    : '';

  const formatDateToForm = (dateStr) => {
    if (!dateStr) {
      return '';
    }
    return moment(dateStr).format('DD/MM/YYYY');
  };

  return {
    ...invoice,
    vatNumber: invoice.vatNumber || '',
    documentType: invoice.invoiceType?.includes('CREDIT')
      ? 'credit'
      : invoice.invoiceType?.includes('DEBIT')
      ? 'debit'
      : 'invoice',
    documentTransactionType: invoice.invoiceType?.includes('SIMPLIFIED')
      ? 'simplified'
      : 'standard',
    invoiceIssueDate,
    invoiceTypeTransaction: invoice.invoiceTypeTransaction || '',
    paidAmount: totalPaidAmount,
    paidAmountSwitch: prepaymentItems.length > 0,
    noteType: invoice.noteType || 'CREDIT_NOTE',
    invoiceReferenceNumber: invoice.invoiceReferenceNumber || '',
    invoiceUniqueIdentifier: invoice.invoiceUniqueIdentifier || '',
    sellerVatNumber: invoice.seller?.vatNumber || '',
    groupVatNumber: invoice.seller?.groupVatNumber || '',
    sellerEnglishName: invoice.seller?.sellerEnglishName || '',
    sellerArabicName: invoice.seller?.sellerArabicName || '',
    additionalSellerIdType: invoice.seller?.additionalSellerIdType || '',
    additionalSellerId: invoice.seller?.additionalSellerId || '',
    sellerStreetEn: invoice.seller?.sellerStreetEn || '',
    sellerStreetAr: invoice.seller?.sellerStreetAr || '',
    additionalSellerStreetEn: invoice.seller?.additionalSellerStreetEn || '',
    additionalSellerStreetAr: invoice.seller?.additionalSellerStreetAr || '',
    sellerBuildingNumber: invoice.seller?.sellerBuildingNumber || '',
    additionalSellerBuildingNumber:
      invoice.seller?.additionalSellerBuildingNumber || '',
    sellerBuildingName: invoice.seller?.sellerBuildingName || '',
    sellerFlatNumber: invoice.seller?.sellerFlatNumber || '',
    sellerBlock: invoice.seller?.sellerBlock || '',
    sellerCity: invoice.seller?.sellerCity || '',
    sellerPostalCode: invoice.seller?.sellerPostalCode || '',
    sellerStateEn: invoice.seller?.sellerStateEn || '',
    sellerStateAr: invoice.seller?.sellerStateAr || '',
    sellerNeighborhoodEn: invoice.seller?.sellerNeighborhoodEn || '',
    sellerNeighborhoodAr: invoice.seller?.sellerNeighborhoodAr || '',
    sellerCountryCode: invoice.seller?.sellerCountryCode || '',
    buyerEnglishName: invoice.buyer?.buyerEnglishName || '',
    buyerArabicName: invoice.buyer?.buyerArabicName || '',
    buyerVatNumber: invoice.buyer?.buyerVatNumber || '',
    buyerGroupVatNumber: invoice.buyer?.buyerGroupVatNumber || '',
    additionalBuyerIdType: invoice.buyer?.additionalBuyerIdType || '',
    additionalBuyerId: invoice.buyer?.additionalBuyerId || '',
    buyerStreetEn: invoice.buyer?.buyerStreetEn || '',
    buyerStreetAr: invoice.buyer?.buyerStreetAr || '',
    additionalBuyerStreetEn: invoice.buyer?.additionalBuyerStreetEn || '',
    additionalBuyerStreetAr: invoice.buyer?.additionalBuyerStreetAr || '',
    buyerBuildingNumber: invoice.buyer?.buyerBuildingNumber || '',
    additionalBuyerBuildingNumber:
      invoice.buyer?.additionalBuyerBuildingNumber || '',
    buyerBuildingName: invoice.buyer?.buyerBuildingName || '',
    buyerFlatNumber: invoice.buyer?.buyerFlatNumber || '',
    buyerBlock: invoice.buyer?.buyerBlock || '',
    buyerCity: invoice.buyer?.buyerCity || '',
    buyerPostalCode: invoice.buyer?.buyerPostalCode || '',
    buyerStateEn: invoice.buyer?.buyerStateEn || '',
    buyerStateAr: invoice.buyer?.buyerStateAr || '',
    buyerNeighborhoodEn: invoice.buyer?.buyerNeighborhoodEn || '',
    buyerNeighborhoodAr: invoice.buyer?.buyerNeighborhoodAr || '',
    buyerCountryCode: invoice.buyer?.buyerCountryCode || '',
    items,
    notesAr: invoice.arNotes || '',
    notesEn: invoice.enNotes || '',
    supplyStartDate: formatDateToForm(invoice.supplyDate),
    supplyEndDate: formatDateToForm(invoice.supplyEndDate) || '',
    purchaseOrderNumber: invoice.purchaseOrderNumber || '',
    salesOrderReference: invoice.salesOrderNumber || '',
    contactNumber: invoice.contactNumber || '',
    specialTaxTreatmentEn: invoice.specialTaxTreatmentEn || '',
    specialTaxTreatmentAr: invoice.specialTaxTreatmentAr || '',
    originalInvoiceReference: invoice.originalInvoiceReference || '',
    originalGrossAmount: invoice.originalGrossAmount || '',
    noteReasonOfIssuance: invoice.noteReasonOfIssuance || '',
    precedingInvoiceNumber: invoice.originalInvoiceReference || '',
    precedingInvoiceIssueDate: formatDateToForm(
      invoice.precedingInvoiceIssueDate,
    ),
    noteIssuingReason: invoice.reasonOfNote || '',
    paymentMethod: invoice.paymentTerms?.paymentMethodCode || 'CASH',
    bankNameEn: invoice.paymentTerms?.bankNameEn || '',
    bankNameAr: invoice.paymentTerms?.bankNameAr || '',
    accountNumber: invoice.paymentTerms?.accountNumber || '',
    paymentTermsAr: invoice.paymentTerms?.paymentTermsAr || '',
    paymentTermsEn: invoice.paymentTerms?.paymentTermsEn || '',
    invoiceDiscountLineItems: invoice.invoiceLevelDiscount || [],
    invoicePrepaymentLineItems: prepaymentItems.map((prepayment) => ({
      id: prepayment.prepaymentInvoiceNumber || '',
      invoiceNumber: prepayment.prepaymentInvoiceNumber || '',
      invoiceIssueDate: formatDateToForm(prepayment.prepaymentInvoiceIssueDate),
      taxableAmount: prepayment.prepaymentTaxableAmount?.toString() || '',
      vatRate:
        getTaxTypes('BH', 'en').find(
          (type) =>
            Number(type.rate) === Number(prepayment.prepaymentVatRate || 0),
        )?.value || '',
    })),
    invoicingPeriodStartDate: formatDateToForm(
      invoice.invoicingPeriodStartDate,
    ),
    invoicingPeriodEndDate: formatDateToForm(invoice.invoicingPeriodEndDate),
    invoiceStatus: invoice.invoiceStatus || 'ACCEPTED',
    currency: invoice.currency || 'BHD',
    logoImage: invoice.logoImage || '',
    outOfScopeReasonInput,
    exemptionReasonCode,
    zeroRatingReasonInput,
    specialScenario: !!(
      invoice.deemedInvoiceTransaction ||
      invoice.exportsInvoiceTransaction ||
      invoice.summaryInvoiceTransaction ||
      invoice.thirdPartyInvoiceTransaction ||
      invoice.selfBilledInvoiceTransaction ||
      invoice.importOfService
    ),
    deemedSupply: !!invoice.deemedInvoiceTransaction,
    summaryEinvoice: !!invoice.summaryInvoiceTransaction,
    throughRCMCheck: !!(
      invoice.exportsInvoiceTransaction || invoice.importOfService
    ),
    throughRCM: invoice.exportsInvoiceTransaction
      ? 'Export'
      : invoice.importOfService
      ? 'Import'
      : '',
    specialBillingCheck: !!(
      invoice.thirdPartyInvoiceTransaction ||
      invoice.selfBilledInvoiceTransaction
    ),
    specialBilling: invoice.thirdPartyInvoiceTransaction
      ? 'Third'
      : invoice.selfBilledInvoiceTransaction
      ? 'Self'
      : '',
    roundingAmount: invoice.roundingAmount?.toString() || '',
    roundingAmountSwitch: !!invoice.roundingAmount,
    paymentDate: invoice.paymentDate
      ? formatDateToForm(invoice.paymentDate)
      : '',
    supplyDate: invoice.supplyDate ? formatDateToForm(invoice.supplyDate) : '',
    exchangeRate: invoice.exchangeRate?.toString() || '',
  };
};

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
